import React, { useState } from "react";
import ToolsCard from "../../misc/ToolsCard";
import ModalComponent from "../../misc/ModalComponent";
import { useTranslation } from "react-i18next";
import ClassroomViewToolDeleteClassroom from "./Tools/ClassroomViewToolDeleteClassroom";
import ClassroomViewToolUpdateOwner from "./Tools/ClassroomViewToolUpdateOwner";
import ClassroomViewToolAddMembers from "./Tools/ClassroomViewToolAddMembers";
import ClassroomViewToolChangeStatus from "./Tools/ClassroomViewToolChangeStatus";
import { AddClassroomMembersType, CoreClassroomAdminClassroom, CoreClassroomAdminCourseState } from "../../../models/CoreClassroomAdminClassroom";
import { RequestState } from "../../../models/RequestState";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
import StatusCode from "../../../util/StatusCode";
import { FlowSyncAccessPolicy, isAuthorized } from "../../../models/FlowSyncAccessPolicy";


export enum InfoType {
    UNDEFINED = 'UNDEFINED',
    DELETE_CLASSROOM = 'DELETE_CLASSROOM',
    UPDATE_OWNER = 'UPDATE_OWNER',
    ADD_MEMBERS = 'ADD_MEMBERS',
    CHANGE_STATUS = 'CHANGE_STATUS'
}

const ClassroomViewTools = () => {
    const { t } = useTranslation(['common', "classroomAdmin"]);

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [info, setInfo] = useState<InfoType>(InfoType.UNDEFINED);
    const [type, setType] = useState<AddClassroomMembersType>();

    const classroom = useSelector<RootState, RequestState<CoreClassroomAdminClassroom>>(x => x.classroomAdminClassroom.classroom);

    const isSynchedClassroom = classroom.code === StatusCode.COMPLETE && classroom.data?.synched === true;
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);
    const hasClassroomEdit = isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.CLASSROOM_ADMIN_EDIT);
    const canChangeStatus = classroom.code === StatusCode.COMPLETE
        && (classroom.data?.synched === false
        || (classroom.data?.synched === true
            && classroom.data.state.courseState === CoreClassroomAdminCourseState[CoreClassroomAdminCourseState.Archived]));

    const renderBody = () => {
        if (classroom.code !== StatusCode.COMPLETE) {
            return;
        }
        switch (info) {
            case InfoType.DELETE_CLASSROOM:
                return <ClassroomViewToolDeleteClassroom classrooms={[classroom.data]} />;
            case InfoType.UPDATE_OWNER:
                return <ClassroomViewToolUpdateOwner classrooms={[classroom.data]} />;
            case InfoType.ADD_MEMBERS:
                return <ClassroomViewToolAddMembers type={type} classrooms={[classroom.data]} />;
            case InfoType.CHANGE_STATUS:
                return <ClassroomViewToolChangeStatus classrooms={[classroom.data]} />;
            case InfoType.UNDEFINED: return null;
            default: return null;
        }
    }

    const showInformation = () => (
        <ModalComponent
            header={t(`tools.${info}.title`, { ns: 'classroomAdmin' })}
            cancel={t('action.close', { ns: 'common' })}
            isOpen={openModal}
            size="lg"
            toggleModal={() => {setOpenModal(false); setInfo(InfoType.UNDEFINED);}}
        >
            {renderBody()}
        </ModalComponent>
    );

    return <div className="tab-border">
        <h5 className="pt-4">{t('tools.title', { ns: 'classroomAdmin' })}</h5>
        <p>{t('tools.description', { ns: 'classroomAdmin' })}</p>
        <div className="toolscard-display pb-5">
            <ToolsCard
                title={t(`tools.${InfoType.DELETE_CLASSROOM}.title`, { ns: 'classroomAdmin' })}
                description={t(`tools.${InfoType.DELETE_CLASSROOM}.description`, { ns: 'classroomAdmin' })}
                onChange={() => {setOpenModal(true); setInfo(InfoType.DELETE_CLASSROOM);}}  
                buttonText={t('tools.delete', { ns: 'classroomAdmin' })}
                disabled={isSynchedClassroom || !hasClassroomEdit}
            />

            <ToolsCard
                title={t(`tools.${InfoType.UPDATE_OWNER}.title`, { ns: 'classroomAdmin' })}
                description={t(`tools.${InfoType.UPDATE_OWNER}.description`, { ns: 'classroomAdmin' })}
                onChange={() => {setOpenModal(true); setInfo(InfoType.UPDATE_OWNER);}}
                buttonText={t(`tools.${InfoType.UPDATE_OWNER}.title`, { ns: 'classroomAdmin' })}
                disabled={isSynchedClassroom || !hasClassroomEdit}
            />

            <ToolsCard
                title={t(`tools.${InfoType.ADD_MEMBERS}.studentTitle`, { ns: 'classroomAdmin' })}
                description={t(`tools.${InfoType.ADD_MEMBERS}.studentDescription`, { ns: 'classroomAdmin' })}
                onChange={() => {setOpenModal(true); setInfo(InfoType.ADD_MEMBERS); setType(AddClassroomMembersType.Student);}}
                buttonText={t(`tools.${InfoType.ADD_MEMBERS}.studentTitle`, { ns: 'classroomAdmin' })}
                disabled={isSynchedClassroom || !hasClassroomEdit}
            />

            <ToolsCard
                title={t(`tools.${InfoType.ADD_MEMBERS}.teacherTitle`, { ns: 'classroomAdmin' })}
                description={t(`tools.${InfoType.ADD_MEMBERS}.teacherDescription`, { ns: 'classroomAdmin' })}
                onChange={() => {setOpenModal(true); setInfo(InfoType.ADD_MEMBERS); setType(AddClassroomMembersType.Teacher);}}
                buttonText={t(`tools.${InfoType.ADD_MEMBERS}.teacherTitle`, { ns: 'classroomAdmin' })}
                disabled={isSynchedClassroom || !hasClassroomEdit}
            />

            <ToolsCard
                title={t(`tools.${InfoType.CHANGE_STATUS}.title`, { ns: 'classroomAdmin' })}
                description={t(`tools.${InfoType.CHANGE_STATUS}.description`, { ns: 'classroomAdmin' })}
                onChange={() => {setOpenModal(true); setInfo(InfoType.CHANGE_STATUS);}}
                buttonText={t(`tools.${InfoType.CHANGE_STATUS}.title`, { ns: 'classroomAdmin' })}
                disabled={!canChangeStatus || !hasClassroomEdit}
            />

        </div>
        {openModal && showInformation()}
    </div>
}

export default ClassroomViewTools;
